import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import DropdownSelect from '../dropdownSelect/DropdownSelect'
import {
  ContentStateInterface,
  ContentStateProperty,
  DropdownOptionSelection,
  ScreenProperty,
} from '../../../../@types/types'
import { loadManualCities, loadManualQuarters, loadManualRegions } from '../../../../redux/thunks/gisService/gisService'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { SCREEN_ADDRESS, SCREENS } from '../../../../services/domain/wizardScreens'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { AddressScreenValidationResult } from '../../../../@types/validation-types'
import { EntityObject } from '../../../../@types/geoAutocompletion-types'

const screen = SCREENS[SCREEN_ADDRESS].name

export type ManualGeoCodingProps = {
  insertionData: ContentStateInterface
  validationMessages?: AddressScreenValidationResult
  onChange: (propertyName: ContentStateProperty, value: DropdownOptionSelection, object?: EntityObject) => void
  doReset?: (clearingUpOf: ScreenProperty[]) => void
}

const ManualGeoCoding: React.FC<ManualGeoCodingProps> = ({ insertionData, validationMessages, onChange, doReset }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { manualGeoCoding } = useAppSelector((initial: InitState) => initial.addressFeature)

  useEffect(() => {
    !manualGeoCoding.regions.length && dispatch(loadManualRegions())
  }, [dispatch, manualGeoCoding.regions])

  useEffect(() => {
    if (insertionData.manualRegion !== undefined) {
      dispatch(loadManualCities(insertionData.manualRegion))
    }
  }, [dispatch, insertionData.manualRegion])

  useEffect(() => {
    if (insertionData.manualRegion !== undefined && insertionData.manualCity !== undefined) {
      dispatch(loadManualQuarters(insertionData.manualRegion, insertionData.manualCity))
    }
  }, [insertionData.manualRegion, insertionData.manualCity, dispatch])

  return (
    <>
      <span className='is24-icon-geo-location margin-right-xs' />
      <span className='font-body-medium-bold'>
        {t('wizard.screen.nationalAddress.manualGeoCoding.geographical', 'Geographical allocation*')}
      </span>

      <div className='margin-top-l one-whole pii-hide'>
        <DropdownSelect
          id='manual-region'
          name='manualRegion'
          placeholder={t(
            'wizard.screen.nationalAddress.manualGeoCoding.regionPlaceholder',
            'Please select region first.'
          )}
          label={'wizard.screen.nationalAddress.manualGeoCoding.region'}
          value={insertionData.manualRegion?.value}
          options={manualGeoCoding.regions}
          onChange={(value: DropdownOptionSelection) => {
            onChange('manualRegion', value)
            doReset &&
              doReset([
                {
                  screen: screen,
                  property: 'manualCity',
                },
                {
                  screen: screen,
                  property: 'manualQuarter',
                },
              ])
          }}
          errors={validationMessages?.manualRegion ?? []}
        />
      </div>

      <div className='margin-top-l one-whole'>
        <DropdownSelect
          id='manual-city'
          name='manualCity'
          placeholder={t(
            'wizard.screen.nationalAddress.manualGeoCoding.cityPlaceholder',
            'Then fill in city/community'
          )}
          label={'wizard.screen.nationalAddress.manualGeoCoding.city'}
          disabled={insertionData.manualRegion?.value === null || insertionData.manualRegion?.value === undefined}
          value={insertionData.manualCity?.value}
          options={manualGeoCoding.cities}
          onChange={(value: DropdownOptionSelection) => {
            onChange('manualCity', value)
            doReset &&
              doReset([
                {
                  screen: screen,
                  property: 'manualQuarter',
                },
              ])
          }}
          errors={validationMessages?.manualCity ?? []}
        />
      </div>

      <div className='margin-top-l one-whole'>
        <DropdownSelect
          id='manual-quarter'
          name='manualQuarter'
          placeholder={t(
            'wizard.screen.nationalAddress.manualGeoCoding.quarterPlaceholder',
            'Then fill in district/municipality'
          )}
          label={'wizard.screen.nationalAddress.manualGeoCoding.quarter'}
          disabled={insertionData.manualCity?.value === null || insertionData.manualCity?.value === undefined}
          value={insertionData.manualQuarter?.value}
          options={manualGeoCoding.quarters}
          onChange={(value: DropdownOptionSelection) => {
            onChange('manualQuarter', value)
          }}
          errors={validationMessages?.manualQuarter ?? []}
        />
      </div>
    </>
  )
}

export default ManualGeoCoding
